import { Download, FileDownload, Share } from '@mui/icons-material';
import { CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import SharePopup from '/@/components/share-popup';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { emailRecommendationPDF, getRecommendationPDF } from '/@/store/private-data/recommendation-pdfs/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Recommendation } from '/@/types/recommendation';
import { seedPdfVersion } from '/@/views/recommendations/seed-pdf-version';
import { toRenderableRecommendation } from '/@/views/recommendations/v2/result/parse-recommendation';
import ModalV2 from '../bayer/modal-v2';
import MoreOptions from '../more-options';
import type { ShareItemPopupMode } from '../share-item-popup';

interface RecommendationShareProps {
    recommendation: Recommendation;
    classNameRoot?: string;
    disabled?: boolean;
    type?: string;
    version?: number;
    /** PDF template to request. Derived from the recommendation when omitted. */
    pdfVersion?: number;
    /**
     * `menu` collapses the actions into an overflow menu, which is how the mobile header
     * presents them. The popup and download flows are identical either way, so they stay
     * owned by this component rather than being lifted into every caller.
     */
    variant?: 'buttons' | 'menu';
    /** Appended to the overflow menu, for actions this component doesn't own. */
    extraMenuItems?: { label: string; onClick: () => void; hidden?: boolean }[];
    entrySource?: string;
}

export default function RecommendationShare({
    recommendation,
    classNameRoot,
    disabled,
    type,
    version = 1,
    pdfVersion: pdfVersionProp,
    variant = 'buttons',
    extraMenuItems = [],
    entrySource,
}: RecommendationShareProps) {
    // Corn seed recommendations render the v2 template; the prop stays an escape hatch.
    const pdfVersion = pdfVersionProp ?? seedPdfVersion(recommendation);
    // The v2 template reads its hybrids off `crops[0]`, which a management recommendation
    // doesn't carry — it is stored as `corn_density`. Resolving it here rather than at each
    // call site is what makes the document identical wherever it was shared from.
    const pdfPayload = useMemo(
        () => (pdfVersion === 2 && recommendation ? toRenderableRecommendation(recommendation) : recommendation),
        [pdfVersion, recommendation]
    );
    const [showPopup, setShowPopup] = useState(false);
    // Version 2 is the only template with selectable sections, so it's the only one whose
    // buttons open the picker.
    const picksBlocks = pdfVersion === 2;
    const [popupMode, setPopupMode] = useState<ShareItemPopupMode | undefined>(undefined);
    const openPopup = (mode: ShareItemPopupMode) => {
        setPopupMode(mode);
        setShowPopup(true);
    };
    const [isDownloading, setIsDownloading] = useState(false);
    const [fileProps, setFileProps] = useState(undefined);
    const dispatch = useDispatch();

    const handleShowPopup = () => {
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    const handleEmail = async ({ email }: { email: string }) => {
        if (!type && recommendation?.weeds?.length > 0) type = 'old-weed';

        const file = await dispatch(emailRecommendationPDF({ recommendation: pdfPayload, email, version: pdfVersion }));

        return {
            file,
            type,
            source: 'recommendation-detail',
            numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
        };
    };

    const handleWhatsApp = async () => {
        if (!type && recommendation?.weeds?.length > 0) type = 'old-weed';

        const file = await dispatch(getRecommendationPDF({ recommendation: pdfPayload, version: pdfVersion }));

        return {
            file,
            type,
            source: 'recommendation-detail',
            numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
        };
    };

    const handleDownload = async () => {
        if (!type && recommendation?.weeds?.length > 0) type = 'old-weed';

        try {
            setIsDownloading(true);
            const file = await dispatch(
                getRecommendationPDF({ recommendation: pdfPayload, download: true, version: pdfVersion })
            );
            setIsDownloading(false);
            setFileProps({
                file,
                type,
                source: 'recommendation-detail',
                numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
            });

            return {
                file,
                type,
                source: 'recommendation-detail',
                numberOfHybrids: recommendation?.crops?.[0]?.productRecommendations?.length,
            };
        } catch (error) {
            setIsDownloading(false);
            setFileProps(undefined);
        }
    };

    // Rendered by both variants: the share popup and the download modal are the same
    // flows whether they were opened from a button or from the overflow menu.
    const popups = (
        <>
            <SharePopup
                visible={showPopup}
                title={_('Compartir recomendación')}
                whatsAppIntro={_('Te envío una recomendación generada en {{ appName }}', {
                    appName: (settings as any).app?.name || 'Cultivio',
                })}
                onClose={handleClosePopup}
                onEmail={handleEmail}
                onWhatsApp={handleWhatsApp}
                type={type}
                version={version}
                recommendation={recommendation}
                entrySource={entrySource}
                pdfVersion={pdfVersion}
                mode={popupMode}
            />
            <ModalV2
                open={isDownloading || fileProps}
                onClose={() => setFileProps(undefined)}
                title={_('Descargar PDF')}
                data-testid='recommendation-share-download-modal'
                actions={
                    <Button
                        startIcon={fileProps ? <Download /> : <CircularProgress size={20} />}
                        loading={isDownloading}
                        disabled={!fileProps}
                        data-testid={`recommendation-share-download-modal-button-${fileProps ? 'download' : 'generating'}`}
                        onClick={async () => {
                            if (fileProps) {
                                // Don't await to update optimistically, email is sent via offline sync mechanism.
                                const { crop, source, numberOfHybrids } = fileProps;
                                if (type === 'crop-protection') {
                                    mpTrackEvent('Crop Protection Recommendation downloaded', {
                                        entry_source: 'my-recommendations',
                                        crop: recommendation.type,
                                        cp_type: recommendation.typeLabel,
                                    });
                                } else if (['corn', 'soy', 'weed'].includes(type)) {
                                    mpTrackEvent('Seed recommendation downloaded', {
                                        crop,
                                        number_of_hybrids: numberOfHybrids,
                                        entry_source: source,
                                        recommendation_type: type === 'weed' ? undefined : 'hybrid',
                                    });
                                }
                                window.open(fileProps.file.url);
                            }
                        }}
                    >
                        {fileProps ? _('Descargar PDF') : _('Generando PDF...')}
                    </Button>
                }
                actionsProps={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                <Typography variant='subtitle1' color='primary'>
                    {!fileProps
                        ? _('Estamos generando el PDF. Una vez listo, podrás descargarlo en unos segundos.')
                        : _('¡Tu recomendación está lista!')}
                </Typography>
            </ModalV2>
        </>
    );

    if (variant === 'menu') {
        return (
            <>
                <MoreOptions
                    menuItems={[
                        {
                            label: picksBlocks ? _('Generar PDF') : _('Descargar'),
                            onClick: () => (picksBlocks ? openPopup('generate') : handleDownload()),
                            disabled,
                        },
                        {
                            label: _('Compartir'),
                            onClick: () => (picksBlocks ? openPopup('share') : handleShowPopup()),
                            disabled,
                        },
                        ...extraMenuItems,
                    ]}
                />
                {popups}
            </>
        );
    }

    return (
        <>
            {version === 1 ? (
                <div className={classNameRoot}>
                    <Button variant='outlined' fullWidth onClick={handleShowPopup} disabled={disabled} color='primary'>
                        <Tooltip
                            title={
                                disabled
                                    ? _('Para poder compartir una recomendación, debes guardarla primero')
                                    : undefined
                            }
                            arrow
                        >
                            <Typography sx={{ lineHeight: 'inherit', fontWeight: 'inherit' }}>
                                {_('Compartir')}
                            </Typography>
                        </Tooltip>
                    </Button>
                </div>
            ) : (
                <Stack direction='row' spacing={1}>
                    <Button
                        variant='outlined'
                        onClick={picksBlocks ? () => openPopup('generate') : handleDownload}
                        disabled={disabled}
                        color='primary'
                        endIcon={<FileDownload />}
                        sx={{ backgroundColor: '#fff' }}
                        data-testid='recommendation-share-download-button'
                    >
                        {picksBlocks ? _('Generar PDF') : _('Descargar')}
                    </Button>
                    <Button
                        variant='contained'
                        onClick={picksBlocks ? () => openPopup('share') : handleShowPopup}
                        disabled={disabled}
                        color='secondary'
                        endIcon={<Share />}
                        data-testid='recommendation-share-share-button'
                    >
                        <Tooltip
                            title={
                                disabled
                                    ? _('Para poder compartir una recomendación, debes guardarla primero')
                                    : undefined
                            }
                            arrow
                        >
                            {picksBlocks ? _('Compartir') : _('Compartir recomendación')}
                        </Tooltip>
                    </Button>
                </Stack>
            )}
            {popups}
        </>
    );
}
